import { useEffect, useRef, type RefObject } from 'react';
import { useUser } from '@/hooks/user/useUser';
import { useFrontendConfigs } from '@/hooks/useFrontendConfigs';
import { UserProfileModel } from '@/apiTypes/UserTypes';
import HeapSetup from '@/lib/heap/heapSetup';
import { buildHeapIdentifyUserProperties, hasHeapUserIds } from '@/lib/heap/buildHeapIdentifyUserProperties';
import { getHeapOnReadyStallMsFromPublicConfig, runWhenHeapReady } from '@/lib/heap/runWhenHeapReady';
import {
    reportHeapIdentifyDeferredError,
    reportHeapIdentifyDeferredStall,
    reportHeapIdentifySkipped,
} from '@/lib/heap/heapIdentifyTelemetry';

/**
 * Mounts with `LoadThirdPartyConfigs` (not `AppRoutes`) so Heap runs with other third-party setup.
 *
 * 1) If public `heapId` is set and the in-page stub is missing, inject the Heap loader.
 * 2) If we can identify (`uid` + `id` + `window.heap`), run `identify` / `addUserProperties` after
 *    `heap.onReady` and emit Groundcover RUM (needs `groundcoverApiKey` in public config) if `onReady` stalls or the callback throws.
 * 3) If the user query is settled, we have a logged-in `user.id`, but identify is impossible,
 *    emit `heap_identify_skipped` once per user (until the account changes).
 */
export const HeapIdentifyEffect = () => {
    const { configs } = useFrontendConfigs();
    const { user, isFetching, isUninitialized } = useUser();
    const userProfile = user?.userProfile as UserProfileModel | undefined;

    const lastSkipReportUserIdRef = useRef<string | undefined>(undefined);
    const heapSkipEventAlreadySentRef = useRef(false);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return () => {};
        }

        clearSkipStateWhenUserChanges(lastSkipReportUserIdRef, heapSkipEventAlreadySentRef, user?.id);

        const heapId = configs['heapId'];
        if (heapId && !window.heap) {
            HeapSetup.initialize(heapId);
        }

        const groundcoverApiKey = configs['groundcoverApiKey'];
        const canRunIdentify = hasHeapUserIds(user) && Boolean(window.heap);

        const clearDeferredStall = canRunIdentify
            ? scheduleIdentifyAfterHeapReady(
                    user,
                    userProfile,
                    configs,
                    groundcoverApiKey,
                )
            : undefined;

        if (
            !isUninitialized
            && !isFetching
            && Boolean(user?.id)
            && !canRunIdentify
            && !heapSkipEventAlreadySentRef.current
        ) {
            heapSkipEventAlreadySentRef.current = true;
            reportHeapIdentifySkipped(groundcoverApiKey, {
                hasHeapId: Boolean(heapId),
                hasUserId: Boolean(user?.id),
                hasUid: Boolean(user?.uid),
                hasSub: Boolean(user?.sub),
                hasWindowHeap: Boolean(window.heap),
            });
        }

        return () => {
            clearDeferredStall?.();
        };
    // Intentionally depend on id fields (and `userProfile`), not `user` object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.sub, user?.uid, userProfile, configs, isFetching, isUninitialized]);

    return null;
};

function clearSkipStateWhenUserChanges(
    lastUserIdRef: RefObject<string | undefined>,
    skipEmittedRef: RefObject<boolean>,
    currentUserId: string | undefined,
): void {
    if (currentUserId === lastUserIdRef.current) {
        return;
    }
    lastUserIdRef.current = currentUserId;
    skipEmittedRef.current = false;
}

function scheduleIdentifyAfterHeapReady(
    user: { uid: string; id: string; sub?: string },
    userProfile: UserProfileModel | undefined,
    configs: { [key: string]: string },
    groundcoverApiKey: string | undefined,
): (() => void) | undefined {
    const properties = buildHeapIdentifyUserProperties(user, userProfile, configs);
    const { uid } = user;
    const stallAfterMs = getHeapOnReadyStallMsFromPublicConfig(configs);

    return runWhenHeapReady(
        () => {
            if (!window.heap) {
                return;
            }
            window.heap.identify(uid);
            window.heap.addUserProperties(properties);
        },
        {
            stallAfterMs,
            onStall: () => {
                reportHeapIdentifyDeferredStall(groundcoverApiKey, stallAfterMs);
            },
            onError: (err) => {
                const msg = err instanceof Error ? err.message : String(err);
                reportHeapIdentifyDeferredError(groundcoverApiKey, msg);
            },
        },
    );
}
